import React from 'react';
import styled from 'styled-components';
import { FiArrowRight } from 'react-icons/fi';
import colors from '../../styles/colors';

const WorkSection = styled.section`
  padding: 120px 40px;
  background: ${colors.background.cream};

  @media (max-width: 768px) {
    padding: 80px 20px;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 70px;
`;

const Subtitle = styled.div`
  color: ${colors.primary.main};
  font-weight: 700;
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 3px;
  margin-bottom: 16px;
`;

const Title = styled.h2`
  font-size: clamp(2rem, 4vw, 3rem);
  color: ${colors.text.heading};
  margin-bottom: 20px;
  font-weight: 800;
`;

const Description = styled.p`
  font-size: 1.15rem;
  color: ${colors.text.body};
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.8;
`;

const FeaturedCard = styled.div`
  background: ${colors.background.white};
  border-radius: 30px;
  overflow: hidden;
  box-shadow: ${colors.shadow};
  display: grid;
  grid-template-columns: 1.2fr 1fr;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const ImagePlaceholder = styled.div`
  min-height: 450px;
  background: url('/images/work.jpg') center/cover no-repeat;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, rgba(26, 58, 26, 0.2) 0%, transparent 100%);
  }

  @media (max-width: 900px) {
    min-height: 300px;
  }
`;

const CardContent = styled.div`
  padding: 50px;
  display: flex;
  flex-direction: column;
  justify-content: center;

  @media (max-width: 768px) {
    padding: 35px 25px;
  }
`;

const CardBadge = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 8px 18px;
  background: rgba(45, 90, 45, 0.1);
  color: ${colors.primary.main};
  font-size: 0.85rem;
  font-weight: 700;
  border-radius: 50px;
  margin-bottom: 20px;
  width: fit-content;
`;

const CardTitle = styled.h3`
  font-size: 1.8rem;
  color: ${colors.text.heading};
  margin-bottom: 16px;
  font-weight: 800;
`;

const CardDesc = styled.p`
  font-size: 1.05rem;
  color: ${colors.text.body};
  line-height: 1.8;
  margin-bottom: 30px;
`;

const StatRow = styled.div`
  display: flex;
  gap: 40px;
  margin-bottom: 30px;
  flex-wrap: wrap;
`;

const StatItem = styled.div``;

const StatNumber = styled.div`
  font-size: 1.6rem;
  font-weight: 800;
  color: ${colors.primary.main};
`;

const StatLabel = styled.div`
  font-size: 0.9rem;
  color: ${colors.text.body};
  font-weight: 500;
`;

const CTALink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 12px;
  padding: 16px 32px;
  background: ${colors.gradient.forest};
  color: ${colors.text.white};
  font-weight: 700;
  font-size: 1rem;
  border-radius: 50px;
  box-shadow: 0 4px 20px rgba(26, 58, 26, 0.3);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
    transition: left 0.5s ease;
  }

  svg {
    transition: transform 0.3s;
  }

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 30px rgba(26, 58, 26, 0.4);

    &::before {
      left: 100%;
    }

    svg {
      transform: translateX(5px);
    }
  }
`;

export default function Work() {
  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <WorkSection id="work">
      <Container>
        <Header>
          <Subtitle>Our Work</Subtitle>
          <Title>Quality You Can See</Title>
          <Description>
            Take a look at some of our recent projects and see why our clients
            trust us with their outdoor spaces.
          </Description>
        </Header>

        <FeaturedCard>
          <ImagePlaceholder />
          <CardContent>
            <CardBadge>Featured Project</CardBadge>
            <CardTitle>Transforming Outdoor Spaces</CardTitle>
            <CardDesc>
              Whether it's a complete landscape renovation or regular lawn maintenance,
              we bring the same level of dedication and craftsmanship to every project.
              Our team works closely with you to bring your vision to life.
            </CardDesc>
            <StatRow>
              <StatItem>
                <StatNumber>30+</StatNumber>
                <StatLabel>Years Experience</StatLabel>
              </StatItem>
              <StatItem>
                <StatNumber>1000+</StatNumber>
                <StatLabel>Projects Completed</StatLabel>
              </StatItem>
            </StatRow>
            <CTALink onClick={() => scrollTo('contact')}>
              Start Your Project <FiArrowRight />
            </CTALink>
          </CardContent>
        </FeaturedCard>
      </Container>
    </WorkSection>
  );
}
